/**
 * Google OAuth sonrası backend'in yönlendireceği ön yüz kökü.
 *
 * Capacitor WebView kökeni çoğunlukla https://localhost — harici Chrome'da OAuth
 * bittiğinde bu adres telefonda ERR_CONNECTION_REFUSED verir.
 *
 * 1) Tercih: REACT_APP_FRONTEND_PUBLIC_URL (npm run build ile bundle'a gömülür)
 * 2) Native + localhost + env gömülmemişse: aşağıdaki yedek (frontend/.env ile aynı tutun)
 *
 * Her yinelemede: cd frontend && npm run build && npx cap sync android
 */
import { Capacitor } from '@capacitor/core';

/** .env içindeki REACT_APP_FRONTEND_PUBLIC_URL ile aynı olmalı (build kırılırsa son çare) */
const NATIVE_LOCALHOST_FALLBACK = 'https://app.dekosta.com';

function trimBase(url) {
  if (typeof url !== 'string') return '';
  return url.trim().replace(/\/+$/, '');
}

function isLoopbackHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}

function isLikelyMobileUa() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

/**
 * @returns {{ ok: true, base: string } | { ok: false, base: '', message: string }}
 */
export function resolveGoogleOAuthRedirectBase() {
  const fromEnv = trimBase(process.env.REACT_APP_FRONTEND_PUBLIC_URL);
  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';

  let originHost = '';
  try {
    originHost = origin ? new URL(origin).hostname : '';
  } catch {
    originHost = '';
  }
  const originLoopback = isLoopbackHost(originHost);

  const isNative = Capacitor.isNativePlatform();
  const mobileUa = isLikelyMobileUa();

  // Production web: kanonik HTTPS kökü (build’e gömülü) önce — localhost origin ile yanlış state riskini azaltır
  if (
    process.env.NODE_ENV === 'production' &&
    !isNative &&
    fromEnv &&
    /^https:\/\//i.test(fromEnv)
  ) {
    try {
      const h = new URL(fromEnv).hostname;
      if (!isLoopbackHost(h)) {
        return { ok: true, base: fromEnv };
      }
    } catch {
      /* aşağıdaki genel mantık */
    }
  }

  let base = '';

  if (isNative) {
    if (fromEnv) {
      base = fromEnv;
    } else if (!originLoopback && origin) {
      base = origin;
    } else {
      // Çoğu APK hatası: REACT_APP build'e hiç girmemiş; localhost state ile Railway'e gidiyordu
      base = trimBase(NATIVE_LOCALHOST_FALLBACK) || NATIVE_LOCALHOST_FALLBACK;
    }
  } else if (mobileUa && originLoopback) {
    // Capacitor benzeri https://localhost (port yok); emülatörde http://localhost:3000 ayırt et
    const capacitorStyleLocal = /^https:\/\/localhost\b/i.test(origin);
    if (capacitorStyleLocal) {
      base = fromEnv || trimBase(NATIVE_LOCALHOST_FALLBACK) || NATIVE_LOCALHOST_FALLBACK;
    } else {
      base = fromEnv || '';
    }
  } else if (fromEnv && originLoopback) {
    base = fromEnv;
  } else {
    base = origin || fromEnv;
  }

  if (!base) {
    return {
      ok: false,
      base: '',
      message:
        'Google girişi: yönlendirme adresi yok. Telefonda localhost kullanıyorsanız frontend/.env içine REACT_APP_FRONTEND_PUBLIC_URL ekleyip npm run build ve npx cap sync android yapın.',
    };
  }

  let baseHost = '';
  try {
    baseHost = new URL(base).hostname;
  } catch {
    return {
      ok: false,
      base: '',
      message: 'REACT_APP_FRONTEND_PUBLIC_URL geçerli bir https:// adresi olmalıdır.',
    };
  }

  if (isLoopbackHost(baseHost) && (isNative || mobileUa)) {
    return {
      ok: false,
      base: '',
      message:
        'OAuth dönüşü hâlâ localhost. Uygulamayı güncel kodla yeniden derleyin: npm run build → npx cap sync android → APK kurulumu.',
    };
  }

  return { ok: true, base };
}

/** @deprecated Tercihen resolveGoogleOAuthRedirectBase kullanın */
export function getOAuthRedirectBase() {
  const r = resolveGoogleOAuthRedirectBase();
  return r.ok ? r.base : '';
}
